import PermissionAwareExportButton from "#src/batteries-included-components/Buttons/PermissionAwareExportButton/PermissionAwareExportButton";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useNavigate } from "#src/Routers/hooks";
import { linkToRoleDetailPage } from "#src/routes/settings/roles-and-permissions/roles/[roleId]/detail";
import { linkToCreateRolePage } from "#src/routes/settings/roles-and-permissions/roles/create";
import { ExceptionUtils } from "#src/utils/exception";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  DataTablePanel,
  HeaderType,
  Pill,
  PillProps,
  StorageKeys,
  useFilters,
  useToast,
} from "@validereinc/common-components";
import {
  DomainAdapterError,
  RoleSchema,
  RoleType,
  RolesAdapter,
} from "@validereinc/domain";
import {
  downloadLink,
  invariant,
  toTitleCaseString,
} from "@validereinc/utilities";
import React, { useMemo } from "react";

export const RoleListTablePanel = ({
  filterConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
}: Pick<
  StorageKeys,
  "filterConfigStorageKey" | "tableConfigStorageKey" | "tableStateStorageKey"
>) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [filters] = useFilters(filterConfigStorageKey);
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "name",
      sortDirection: "desc",
    },
    filters,
    tableStateStorageKey
  );
  const queryPayload = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters,
  } satisfies Parameters<typeof RolesAdapter.getList>[0];
  const rolesQuery = useQuery(
    ["roles", queryPayload],
    () => RolesAdapter.getList(queryPayload),
    {
      staleTime: 3 * 60 * 1000,
    }
  );
  const rolesMembersQueries = useQueries({
    queries:
      rolesQuery.data?.data.map((role) => ({
        queryKey: ["roles", role.id, "members"],
        queryFn: () => RolesAdapter.members.getMany({ roleId: role.id }),
        staleTime: 3 * 60 * 1000,
      })) ?? [],
  });
  const rolesPermissionsQueries = useQueries({
    queries:
      rolesQuery.data?.data.map((role) => ({
        queryKey: ["roles", role.id, "permissions"],
        queryFn: () => RolesAdapter.permissions.getMany({ id: role.id }),
        staleTime: 3 * 60 * 1000,
      })) ?? [],
  });

  const rolesMembersCounts = useMemo(() => {
    return rolesMembersQueries.reduce<Record<string, number>>((map, query) => {
      if (!query.data?.length) {
        return map;
      }

      map[query.data[0].group_id] = query.data?.length ?? 0;
      return map;
    }, {});
  }, [rolesMembersQueries]);
  const groupsPermissionsCounts = useMemo(() => {
    return rolesPermissionsQueries.reduce<Record<string, number>>(
      (map, query) => {
        if (!query.data?.length) {
          return map;
        }

        map[query.data[0].group_id] = query.data?.length ?? 0;
        return map;
      },
      {}
    );
  }, [rolesPermissionsQueries]);

  const headers: Array<HeaderType<RoleType>> = [
    {
      label: "Name",
      key: RoleSchema.keyof().Enum.name,
      isSortable: true,
      renderComponent: ({ item }) => {
        const roleId = item?.id;

        return roleId ? (
          <RoutingLink to={linkToRoleDetailPage(roleId)}>
            {item.name}
          </RoutingLink>
        ) : (
          "-"
        );
      },
    },
    {
      label: "Status",
      isSortable: true,
      key: RoleSchema.keyof().Enum.status,
      renderComponent: ({ item }) => (
        <Pill
          variant={
            (
              {
                active: "success",
                disabled: "default",
              } satisfies Record<RoleType["status"], PillProps["variant"]>
            )[item.status]
          }
        >
          {toTitleCaseString(item.status, {
            intent: "ui",
            exceptions: ["oil-and-gas"],
          })}
        </Pill>
      ),
    },
    {
      label: "Description",
      key: RoleSchema.keyof().Enum.description,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.TextCell value={item.description} />
      ),
    },
    {
      label: "Members",
      key: "users",
      renderComponent: ({ item }) => {
        return (
          <RoutingLink
            to={{
              pathname: linkToRoleDetailPage(item.id),
              search: "?tab=members",
            }}
          >
            {Object.hasOwn(rolesMembersCounts, item.id)
              ? rolesMembersCounts[item.id]
              : "-"}
          </RoutingLink>
        );
      },
    },
    {
      label: "Permissions",
      key: "permissions",
      renderComponent: ({ item }) => {
        return (
          <RoutingLink
            to={{
              pathname: linkToRoleDetailPage(item.id),
              search: "?tab=permissions",
            }}
          >
            {Object.hasOwn(groupsPermissionsCounts, item.id)
              ? groupsPermissionsCounts[item.id]
              : "-"}
          </RoutingLink>
        );
      },
    },
    {
      label: "Created At",
      key: RoleSchema.keyof().Enum.created_at,
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.created_at}
          withTime
        />
      ),
    },
  ];

  const { mutate: handleExport, isLoading: isExporting } = useMutation({
    mutationFn: async () => {
      const report = await RolesAdapter?.exportList?.(queryPayload);

      invariant(
        report?.s3_download_link,
        "Could not fetch download URL for roles export"
      );

      downloadLink(report.s3_download_link, report.name);
    },
    onError: (error) => {
      const normalizedError = new DomainAdapterError(
        "Failed to export roles list:",
        {
          cause: error,
        }
      );
      ExceptionUtils.reportException(normalizedError);
      toast.push({
        intent: "error",
        description: normalizedError.getExplanation(),
      });
    },
  });

  const actionRowWhenNoRowsSelected = [
    <PermissionAwareExportButton
      key="export-roles"
      onClick={handleExport}
      isExporting={isExporting}
    />,
    <Button
      variant="primary"
      key="add-role"
      onClick={() => navigate({ pathname: linkToCreateRolePage() })}
      iconPosition="left"
      icon="plus-circle"
    >
      Create Role
    </Button>,
  ];

  return (
    <DataTablePanel
      storageKey={tableConfigStorageKey}
      panelProps={{ title: "Roles", actionRow: actionRowWhenNoRowsSelected }}
      dataTableProps={{
        headers,
        items: rolesQuery.data?.data ?? [],
        getItemId: (item) => item.id,
        isLoading:
          rolesQuery.isLoading ||
          rolesMembersQueries.some((q) => q.isLoading) ||
          rolesPermissionsQueries.some((q) => q.isLoading),
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: rolesQuery.data?.total_entries,
          pageSizeText: "roles per page",
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
      }}
    />
  );
};
